/* eslint-disable */
//
// voiceParser.js
//
// Pure, side-effect-free parsing of a spoken phrase into Grass round scores.
// Designed to be forgiving of the natural, non-exact way people speak.
//
// Example phrases that should all parse correctly:
//   "unprotected 10 protected 50 penalties 100 banker no"
//   "ten unprotected, fifty protected, hundred penalties, no banker"
//   "unprotect 15 protect 20 pens 5 banker"          -> banker = yes
//   "protected 50"                                   -> others default to 0
//   "banker yes unprotected forty"                   -> flexible order
//
// The returned object always contains all four fields so the caller can
// populate the form deterministically.
//

// --- Configuration -------------------------------------------------------

// The numeric fields we recognise. Order here is used as the canonical order.
export const NUMERIC_FIELDS = ["Unprotected", "Protected", "Penalties"];

// Synonyms / mis-hearings mapped to the canonical field name. All lower-case.
// Keep these reasonably broad to absorb speech-to-text quirks.
const FIELD_SYNONYMS = {
    // Unprotected
    unprotected: "Unprotected",
    unprotect: "Unprotected",
    "un protected": "Unprotected",
    "un protect": "Unprotected",
    unprotects: "Unprotected",
    unprotection: "Unprotected",
    open: "Unprotected",

    // Protected
    protected: "Protected",
    protect: "Protected",
    protects: "Protected",
    protection: "Protected",
    prot: "Protected",

    // Penalties
    penalties: "Penalties",
    penalty: "Penalties",
    penalt: "Penalties",
    penalize: "Penalties",
    penalized: "Penalties",
    pens: "Penalties",
    pen: "Penalties",
    fines: "Penalties",
    fine: "Penalties",
};

// Banker keyword synonyms.
const BANKER_WORDS = ["banker", "bank", "banked", "banking"];

// Words that negate a banker mention.
const NEGATIVE_WORDS = ["no", "not", "none", "false", "negative", "nope", "without"];

// Words that affirm a banker mention (when present, banker = yes explicitly).
const AFFIRMATIVE_WORDS = ["yes", "yep", "yeah", "true", "affirmative", "yup", "ok", "okay"];

// --- Number word handling ------------------------------------------------

const SMALL_NUMBERS = {
    zero: 0, oh: 0, nought: 0, nil: 0,
    one: 1, two: 2, three: 3, four: 4, five: 5,
    six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15,
    sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19,
};

const TENS = {
    twenty: 20, thirty: 30, forty: 40, fourty: 40, fifty: 50,
    sixty: 60, seventy: 70, eighty: 80, ninety: 90,
};

const MAGNITUDES = {
    hundred: 100,
    thousand: 1000,
};

// Convert a sequence of number-words into a single integer.
// Handles things like "one hundred and twenty five" -> 125, "fifty" -> 50.
function wordsToNumber(tokens) {
    let current = 0;
    let total = 0;
    let used = false;

    for (const raw of tokens) {
        const t = raw.toLowerCase();
        if (t === "and") continue; // "one hundred and five"
        if (t in SMALL_NUMBERS) {
            current += SMALL_NUMBERS[t];
            used = true;
        } else if (t in TENS) {
            current += TENS[t];
            used = true;
        } else if (t in MAGNITUDES) {
            const mag = MAGNITUDES[t];
            if (current === 0) current = 1; // "hundred" alone = 100
            current *= mag;
            total += current;
            current = 0;
            used = true;
        } else {
            break; // not a number word; stop
        }
    }
    if (!used) return null;
    return total + current;
}

// --- Helpers -------------------------------------------------------------

// Round to the nearest multiple of 5 (the game's rule for score inputs).
export function roundToFive(n) {
    return Math.round(n / 5) * 5;
}

// Tokenise a transcript into clean lower-case words.
// Numbers like "10" stay as their own tokens.
function tokenize(transcript) {
    return String(transcript || "")
        .toLowerCase()
        // turn punctuation into spaces so "10," or "no." split cleanly
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/)
        .filter(Boolean);
}

// Resolve a token (or 2-word combo) to a canonical field name, else null.
function resolveField(token) {
    return FIELD_SYNONYMS[token] || null;
}

// Try to read a number starting at index i. Returns { value, nextIndex } or null.
function readNumberAt(tokens, i) {
    const tok = tokens[i];
    if (tok === undefined) return null;

    // Digits: "10", "100"
    if (/^\d+$/.test(tok)) {
        return { value: parseInt(tok, 10), nextIndex: i + 1 };
    }

    // Number words, possibly spanning several tokens ("one hundred and five")
    let j = i;
    while (
        j < tokens.length &&
        (tokens[j] in SMALL_NUMBERS ||
            tokens[j] in TENS ||
            tokens[j] in MAGNITUDES ||
            tokens[j] === "and")
    ) {
        j++;
    }
    if (j > i) {
        const value = wordsToNumber(tokens.slice(i, j));
        if (value !== null) return { value, nextIndex: j };
    }
    return null;
}

// --- Main parser ---------------------------------------------------------

/**
 * Parse a spoken transcript into Grass round scores.
 *
 * @param {string} transcript - raw recognised speech
 * @param {object} [opts]
 * @param {boolean} [opts.roundToMultipleOfFive=true] - round numeric values to nearest 5
 * @returns {{
 *   Unprotected: number,
 *   Protected: number,
 *   Penalties: number,
 *   Banker: boolean,
 *   rounded: { Unprotected?: number, Protected?: number, Penalties?: number },
 *   recognized: { Unprotected: boolean, Protected: boolean, Penalties: boolean, Banker: boolean },
 *   transcript: string
 * }}
 */
export function parseScores(transcript, opts = {}) {
    const roundToMultipleOfFive = opts.roundToMultipleOfFive !== false;
    const tokens = tokenize(transcript);

    const result = {
        Unprotected: 0,
        Protected: 0,
        Penalties: 0,
        Banker: false,
        rounded: {},
        recognized: {
            Unprotected: false,
            Protected: false,
            Penalties: false,
            Banker: false,
        },
        transcript: String(transcript || ""),
    };

    // Tracks token indices already consumed as a number, so a single spoken
    // number binds to exactly one field (e.g. in "unprotected 10 protected 50"
    // the "10" must not also be claimed by "protected").
    const consumed = new Set();
    const rangeFree = (from, to) => {
        for (let k = from; k < to; k++) if (consumed.has(k)) return false;
        return true;
    };
    const markConsumed = (from, to) => {
        for (let k = from; k < to; k++) consumed.add(k);
    };

    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];

        // --- Banker handling ---
        if (BANKER_WORDS.includes(token)) {
            result.recognized.Banker = true;
            // Look at the word before and after for negation/affirmation.
            const prev = tokens[i - 1];
            const next = tokens[i + 1];
            let isNo = false;
            if (prev && NEGATIVE_WORDS.includes(prev)) isNo = true;
            if (next && NEGATIVE_WORDS.includes(next)) isNo = true;
            // "banker yes" or bare "banker" => yes
            result.Banker = !isNo;
            continue;
        }

        // --- Field + number handling ---
        const field = resolveField(token);
        if (field) {
            let value = null;

            // Prefer a number that immediately PRECEDES the field word
            // ("ten unprotected"), provided it hasn't already been claimed.
            const before = readNumberBefore(tokens, i);
            if (before && rangeFree(before.startIndex, i)) {
                value = before.value;
                markConsumed(before.startIndex, i);
            } else {
                // Otherwise look for a number AFTER the field word ("protected 50").
                const after = readNumberAt(tokens, i + 1);
                if (after && rangeFree(i + 1, after.nextIndex)) {
                    value = after.value;
                    markConsumed(i + 1, after.nextIndex);
                }
            }

            if (value !== null) {
                if (roundToMultipleOfFive) {
                    const r = roundToFive(value);
                    if (r !== value) result.rounded[field] = value; // remember original
                    value = r;
                }
                result[field] = value;
                result.recognized[field] = true;
            } else {
                // Field mentioned with no number => treat as recognised, value 0.
                result.recognized[field] = true;
            }
        }
    }

    return result;
}

// Read a number that appears immediately before index i (scanning left).
function readNumberBefore(tokens, i) {
    // Find the end of a number sequence that ends at i-1.
    let end = i; // exclusive
    let start = i - 1;
    if (start < 0) return null;

    const isNumWord = (t) =>
        /^\d+$/.test(t) ||
        t in SMALL_NUMBERS ||
        t in TENS ||
        t in MAGNITUDES ||
        t === "and";

    if (!isNumWord(tokens[start])) return null;
    while (start - 1 >= 0 && isNumWord(tokens[start - 1])) start--;

    // Single digit token
    const slice = tokens.slice(start, end);
    if (slice.length === 1 && /^\d+$/.test(slice[0])) {
        return { value: parseInt(slice[0], 10), startIndex: start };
    }
    const value = wordsToNumber(slice);
    if (value !== null) return { value, startIndex: start };
    return null;
}
